import { request, loadWorkspace } from "../../services/api.js";
import { dateCalendarClick } from "../../components/date-picker.js";
import { ui, S, save } from "../store.js";
import { render } from "../shell.js";
import {
  signOut,
  openAuthenticatorEnrollment,
  openPasswordChange,
} from "../../features/auth-page.js";
import { toast } from "../../components/ui.js";
import {
  can,
  emp,
  canInvoice,
  canCompany,
  me,
  leaveScope,
} from "../../services/access.js";
import { guardChange } from "../../features/admin/page.js";
import { sendUserInvitation } from "../../features/admin/workspace-tools.js";
import { log } from "../../services/audit.js";
import { fmtD, today, addDays, shortD } from "../../domain/dates.js";
import { syncDraft, depActiveIn } from "../../features/deployments/page.js";
import { proj, inSite } from "../../features/sites/model.js";
import {
  minWage,
  depPay,
  postAccrual,
  postRelease,
  runTot,
  runLines,
} from "../../domain/payroll.js";
import { peso } from "../../domain/format.js";
import { readJV } from "../../features/accounting/voucher-form.js";
import { ACC, PERMS, DEFAULT_ROLES } from "../../domain/reference.js";
import { invDefaults, payInvoice } from "../../features/invoices/model.js";
import { invoiceIdentityError } from "../../domain/invoicing.js";
export function installClickEvents() {
  document.addEventListener("click", (ev) => {
    if (dateCalendarClick(ev)) return;
    const b = ev.target.closest("button");
    if (!b) return;
    if (b.dataset.homeSite) {
      ui.site = b.dataset.homeSite;
      ui.view = "sites";
      return render();
    }
    if (b.dataset.go) {
      const m = {
        gl: ["books", "ledger", "vouchers"],
        fs: ["books", "ledger", "is"],
        bir: ["bir"],
      }[b.dataset.go];
      if (m) {
        ui.view = "accounting";
        ui.acc = m[0];
        if (m[1]) {
          ui.bsec = m[1];
          ui.lv = m[2];
        }
      } else ui.view = b.dataset.go;
      ui.err = "";
      ui.invOpen = null;
      window.scrollTo?.(0, 0);
      return render();
    }
    if (b.dataset.seg) {
      ui[b.dataset.seg] = b.dataset.val;
      if (b.dataset.bsec) ui.bsec = b.dataset.bsec;
      return render();
    }
    const a = b.dataset.act,
      id = b.dataset.id;
    if (!a || a === "role") return;
    const run = S.runs.find((r) => r.id === id),
      j = S.je.find((x) => x.id === id),
      lv = S.leaves.find((x) => x.id === id);
    switch (a) {
      case "home-tasks":
        ui.homeExpanded = !ui.homeExpanded;
        break;
      case "logout":
        void signOut();
        return;
      case "reset":
        toast("Use the workspace import tools to replace data after a backup.");
        return;
      case "reset-ask":
        ui.resetAsk = true;
        break;
      case "reset-no":
        ui.resetAsk = false;
        break;
      case "u-invite":
        if (!can("admin.users")) return;
        ui.invUser = {};
        ui.uErr = "";
        break;
      case "u-cancel":
        ui.invUser = null;
        break;
      case "u-suspend":
      case "u-reactivate": {
        if (!can("admin.users")) return;
        const u = S.users.find((x) => x.id === id),
          ns = a === "u-suspend" ? "SUSPENDED" : "ACTIVE",
          g = guardChange(u, u.role, ns);
        if (g) {
          toast(g);
          return;
        }
        const was = u.status;
        u.status = ns;
        log(
          ns === "SUSPENDED"
            ? was === "INVITED"
              ? "Revoked invitation"
              : "Suspended user"
            : "Reactivated user",
          `${u.name} (${u.email})`,
        );
        toast(
          ns === "SUSPENDED"
            ? `${u.name} can no longer sign in`
            : `${u.name} can sign in again`,
        );
        break;
      }
      case "change-password":
        ui.passwordMinimum = Math.max(12, S.settings.pwMin || 12);
        openPasswordChange();
        return;
      case "my-mfa":
        openAuthenticatorEnrollment(ui.securityUser || me(), {
          replace: !!me().mfa,
        });
        return;
      case "u-mfa":
        toast("Each user enrolls their own authenticator when signing in.");
        return;
      case "u-resend":
        if (!can("admin.users")) return;
        ui.view = "settings";
        ui.settingsTab = "users";
        ui.activationUser = id;
        void sendUserInvitation(id);
        return;
      case "u-signout": {
        if (!can("admin.users")) return;
        void request("/api/accounts/" + encodeURIComponent(id) + "/revoke", {
          method: "POST",
          body: "{}",
        })
          .then(() => toast("User sessions ended."))
          .catch((e) => toast(e.message));
        return;
      }
      case "signout-all": {
        if (!can("sys.admin")) return;
        void request("/api/sessions/revoke", { method: "POST", body: "{}" })
          .then(() => location.reload())
          .catch((e) => toast(e.message));
        return;
      }
      case "backup": {
        if (!can("sys.admin")) return;
        void request("/api/backup", { method: "POST", body: "{}" })
          .then(async (result) => {
            await loadWorkspace();
            render();
            toast("Backup saved: " + result.file);
          })
          .catch((e) => toast(e.message));
        return;
      }
      case "export":
        if (!can("sys.admin")) return;
        log("Exported data", "Full JSON copied to clipboard");
        navigator.clipboard?.writeText(JSON.stringify(S, null, 1)).then(
          () => toast("All data copied as JSON"),
          () => toast("Copy was blocked by the browser"),
        );
        break;
      case "lock-set":
        if (!can("sys.admin") && !can("period.lock")) return;
        S.lockedThrough = id;
        log("Locked period", `Books locked through ${fmtD(id)}`);
        toast(`Books locked through ${fmtD(id)}`);
        break;
      case "go-reg":
        ui.view = "accounting";
        ui.acc = "overview";
        ui.coEdit = true;
        ui.coErr = "";
        break;
      case "dep-new": {
        if (!can("dep.manage")) return;
        if (!S.projects.length)
          return toast("Create a site and project before deploying personnel.");
        const free = S.employees.filter((e) => e.pay === "FIELD");
        ui.depDraft = {
          emp: free[0]?.id || "NEW",
          project: S.projects[0].code,
          type: "DAILY",
          from: today(),
          to: "",
          pd: "0",
          contract: false,
        };
        ui.depErr = "";
        break;
      }
      case "dep-cancel":
        ui.depDraft = null;
        ui.depErr = "";
        break;
      case "dep-end": {
        if (!can("dep.manage")) return;
        const d = S.deployments.find((x) => x.id === id);
        d.status = "ENDED";
        d.endedOn = today();
        syncDraft();
        log(
          "Ended deployment",
          `${emp(d.emp).name} at ${proj(d.project).site}; DOLE report due ${fmtD(addDays(d.endedOn, 30))}`,
        );
        toast(
          `${emp(d.emp).name}'s deployment ended. File the DOLE report within 30 days.`,
        );
        break;
      }
      case "dep-extend": {
        if (!can("dep.manage")) return;
        const d = S.deployments.find((x) => x.id === id),
          was = d.to;
        d.to = addDays(was > today() ? was : today(), 30);
        d.contract = false;
        log(
          "Extended deployment",
          `${emp(d.emp).name}: ${fmtD(was)} → ${fmtD(d.to)}; contract extension to be signed`,
        );
        toast(`Extended to ${fmtD(d.to)}. Have the extension signed.`);
        break;
      }
      case "dep-contract": {
        if (!can("dep.manage")) return;
        const d = S.deployments.find((x) => x.id === id);
        d.contract = true;
        log(
          "Project contract signed",
          `${emp(d.emp).name} for ${d.project} until ${fmtD(d.to)}`,
        );
        toast("Contract recorded");
        break;
      }
      case "dep-osh": {
        if (!can("dep.manage")) return;
        const e = emp(id);
        e.osh = true;
        log("OSH orientation done", e.name);
        toast("OSH orientation recorded");
        break;
      }
      case "dep-report": {
        if (!can("dep.manage")) return;
        const d = S.deployments.find((x) => x.id === id);
        d.report = today();
        log(
          "Filed DOLE termination report",
          `${emp(d.emp).name}, project ${d.project} ended ${fmtD(d.endedOn)}`,
        );
        toast("DOLE report recorded");
        break;
      }
      case "dep-raise": {
        if (!can("dep.manage")) return;
        const d = S.deployments.find((x) => x.id === id),
          mw = minWage(proj(d.project).region, today());
        log(
          "Raised rate to minimum",
          `${emp(d.emp).name}: ${peso(d.rate)} → ${peso(mw.daily)} (${mw.src})`,
        );
        d.rate = mw.daily;
        syncDraft();
        toast(`Rate raised to ${peso(mw.daily)} a day`);
        break;
      }
      case "att":
        ui.view = "worklogs";
        render();
        return;
      case "dep-bill": {
        if (!canInvoice()) return;
        const [c, from] = id.split("|"),
          p = proj(c),
          r = S.runs.find((x) => x.from === from),
          os = depActiveIn(r.from, r.to)
            .filter((d) => d.project === c)
            .map((d) => ({ d, x: depPay(S, d, r.from, r.to) })),
          b = os.reduce((a, o) => a + o.x.bill, 0),
          md = os.reduce((a, o) => a + o.x.worked, 0);
        ui.invDraft = {
          client: p.cid,
          project: c,
          date: today(),
          ret: "0",
          svc: "WC120",
          amount: (b / 100).toFixed(2),
          desc: `Manpower and site works, ${c} ${p.site}, ${shortD(r.from)} – ${fmtD(r.to)}: ${os.length} personnel, ${md} man-days`,
          depKey: id,
        };
        ui.invErr = "";
        ui.view = "accounting";
        ui.acc = "invoices";
        window.scrollTo?.(0, 0);
        break;
      }
      case "jv-new":
        if (!can("gl.create")) return;
        ui.jvDraft = {
          date: today(),
          project: S.projects.find((p) => inSite(p.code))?.code || "",
          memo: "",
          lines: [
            { a: "5220", d: "", c: "" },
            { a: "2110", d: "", c: "" },
          ],
        };
        ui.err = "";
        break;
      case "jv-line":
        readJV();
        ui.jvDraft.lines.push({
          a: "5910" in ACC ? "5910" : "5240",
          d: "",
          c: "",
        });
        break;
      case "jv-cancel":
        ui.jvDraft = null;
        ui.err = "";
        break;
      case "inv-new":
      case "inv-prep": {
        if (!canInvoice()) return;
        const p =
          a === "inv-prep"
            ? S.projects.find((x) => x.code === id)
            : S.projects.find((x) => inSite(x.code) && x.recog > x.billed) ||
              S.projects.find((x) => inSite(x.code)) ||
              S.projects[0];
        ui.invDraft = {
          client: p?.cid || S.clients[0]?.id || "NEW",
          date: today(),
          ret: "7.5",
          ...invDefaults(p?.code),
        };
        ui.invErr = "";
        ui.view = "accounting";
        ui.acc = "invoices";
        window.scrollTo?.(0, 0);
        break;
      }
      case "inv-cancel":
        ui.invDraft = null;
        ui.invErr = "";
        break;
      case "inv-view":
        ui.invOpen = id;
        ui.view = "accounting";
        ui.acc = "invoices";
        break;
      case "inv-print": {
        const invoice = S.invoices.find((item) => item.no === id);
        if (!invoice || invoiceIdentityError(invoice)) return;
        window.print();
        return;
      }
      case "inv-close":
        ui.invOpen = null;
        break;
      case "inv-pay":
        if (!canInvoice()) return;
        payInvoice(id);
        toast(`Payment recorded on ${id}`);
        break;
      case "f2307": {
        if (!canInvoice()) return;
        const i = S.invoices.find((x) => x.no === id);
        i.f2307 = "RECEIVED";
        log("2307 received", `${i.buyer?.name || "Client"} for ${i.no}`);
        toast("Form 2307 marked received");
        break;
      }
      case "co-edit":
        if (!canCompany()) return;
        ui.coEdit = true;
        ui.coErr = "";
        break;
      case "co-cancel":
        ui.coEdit = false;
        break;
      case "csv": {
        const t = document.getElementById(id);
        if (!t) return;
        const csv = [...t.querySelectorAll("tr")]
          .map((r) =>
            [...r.children]
              .map(
                (c) =>
                  '"' +
                  c.innerText.replace(/\s+/g, " ").trim().replace(/"/g, '""') +
                  '"',
              )
              .join(","),
          )
          .join("\n");
        navigator.clipboard?.writeText(csv).then(
          () => toast("Copied as CSV. Paste it into Excel or Sheets."),
          () =>
            toast("Copy was blocked. Select the table and copy it instead."),
        );
        return;
      }
      case "jv-post":
        if (!can("gl.approve") || j.by === ui.user) return;
        if (
          j.lines.reduce((x, l) => x + l.d, 0) > S.settings.jvLimit &&
          !["OWNER", "SUPER"].includes(me().role)
        ) {
          toast(
            `Vouchers above ${peso(S.settings.jvLimit)} need Owner or Superadmin approval`,
          );
          return;
        }
        if (j.date <= S.lockedThrough) {
          toast(`Books are locked through ${fmtD(S.lockedThrough)}`);
          return;
        }
        j.status = "POSTED";
        j.ap = ui.user;
        log("Posted voucher", `${j.id} ${j.memo}`);
        toast(`${j.id} posted`);
        break;
      case "lock":
        if (!can("period.lock")) return;
        S.lockedThrough = "2026-09-30";
        log("Locked period", "Books locked through 30 Sep 2026");
        toast("Books locked through 30 Sep 2026");
        break;
      case "file":
        if (!can("bir.file")) return;
        S.filed[id] = today();
        log("Return filed", id.replace("|", " "));
        toast("Marked as filed");
        break;
      case "lv-ok":
      case "lv-no":
        if (!leaveScope().includes(lv)) return;
        lv.status = a === "lv-ok" ? "APPROVED" : "REJECTED";
        lv.by = ui.user;
        log(
          a === "lv-ok" ? "Approved leave" : "Rejected leave",
          `${emp(lv.emp).name} ${lv.type} ${lv.from}`,
        );
        toast(`Leave ${lv.status.toLowerCase()}`);
        break;
      case "pr-ok":
        if (!can("pay.approve") || run.by === ui.user) return;
        run.status = "APPROVED";
        run.ap = ui.user;
        postAccrual(S, run);
        log("Approved payroll", `${run.id}; accrual posted`);
        toast(`${run.id} approved, accrual posted`);
        break;
      case "pr-rel":
        if (!can("pay.release")) return;
        run.status = "RELEASED";
        run.rel = ui.user;
        postRelease(S, run);
        log("Released payroll", `${run.id}; net ${peso(runTot(run).net)}`);
        toast(`${run.id} released`);
        break;
      case "pr-recalc":
        if (!can("pay.prepare")) return;
        run.lines = runLines(S, run.from, run.to);
        log("Recomputed payroll", run.id);
        toast("Recomputed from salaries and the time records");
        break;
      case "perm": {
        const r = S.roles[b.dataset.r],
          p = b.dataset.p;
        if (!can("admin.users") || b.dataset.r === "SUPER") return;
        r.perms = r.perms.includes(p)
          ? r.perms.filter((x) => x !== p)
          : [...r.perms, p];
        log(
          "Changed permission",
          `${r.name}: ${PERMS[p]} ${r.perms.includes(p) ? "granted" : "removed"}`,
        );
        break;
      }
      case "roles-reset":
        S.roles = JSON.parse(JSON.stringify(DEFAULT_ROLES));
        log("Reset permissions", "Default role matrix restored");
        break;
    }
    save();
    render();
  });
}
